import type { ExerciseTypeConfig } from '../registry/types';
import type { WrittenExerciseDefinition, WrittenExerciseSubmission } from '../types';
import { writtenSubmissionSchema } from '../types/schemas';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: WrittenExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * LongAnswer exercise type configuration
 */
export const longAnswerConfig: ExerciseTypeConfig<
  'LongAnswer',
  WrittenExerciseDefinition,
  WrittenExerciseSubmission,
  { kind: string }
> = {
  type: 'LongAnswer',
  isAutoGradable: false,
  submissionSchema: writtenSubmissionSchema,

  defaultMaxScore: (definition) => {
    return getNormalizedMaxScore(definition, 100);
  },

  // No evaluate function for non-auto-gradable types

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    return {
      format: details.format ?? 'plain',
      textType: details.textType ?? 'long',
    };
  },

  createDefault: (order) => ({
    type: 'LongAnswer' as const,
    format: 'plain' as const,
    textType: 'long' as const,
  }),
};

/**
 * ShortAnswer exercise type configuration
 */
export const shortAnswerConfig: ExerciseTypeConfig<
  'ShortAnswer',
  WrittenExerciseDefinition,
  WrittenExerciseSubmission,
  { kind: string }
> = {
  type: 'ShortAnswer',
  isAutoGradable: false,
  submissionSchema: writtenSubmissionSchema,

  defaultMaxScore: (definition) => {
    return getNormalizedMaxScore(definition, 100);
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    return {
      format: details.format ?? 'plain',
      textType: details.textType ?? 'short',
    };
  },

  createDefault: (order) => ({
    type: 'ShortAnswer' as const,
    format: 'plain' as const,
    textType: 'short' as const,
  }),
};
